import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { AppContext } from '../../../contexts/AppContext';
import useReactConfirmAlert from '../../../hooks/useReactConfirmAlert';
import useProjectService from '../../../app/services/hooks/useProjectService';
import useCostCenterService from '../../../app/services/hooks/useCostCenterService';
import { ProjectDataForm, Product, ProjectUserOption, normalizeProjectPayloadForApi } from '../../../app/models/Project';
import { CostCenterDataForm, CostCenterFilters } from '../../../app/models/CostCenter';
import ProjectFormContainer from './ProjectFormContainer';
import { toast } from 'react-toastify';

interface Props {
  onSaved?: () => void;
  onCancel?: () => void;
}

const NOOP = () => undefined;

const defaultCostCenterFilter: CostCenterFilters = {
  page: 1,
  per_page: 500,
  sort: 'name',
  order: 'asc'
};

const ProjectCreate = ({ onSaved, onCancel = NOOP }: Props) => {
  const { showLoading, hideLoading } = useContext(AppContext);
  const { createProject, storeProject, fetchingCreateProject, fetchingStoreProject } = useProjectService();
  const { getCostCenters } = useCostCenterService();

  const [project, setProject] = useState<ProjectDataForm>({ name: '', description: '' });
  const [products, setProducts] = useState<Product[]>([]);
  const [users, setUsers] = useState<ProjectUserOption[]>([]);
  const [costCenters, setCostCenters] = useState<CostCenterDataForm[]>([]);
  const [errorFields, setErrorFields] = useState<any>();

  useEffect(() => { loadInitialData(); }, []);

  const loadInitialData = () => {
    showLoading?.('loading', 'Cargando datos...');
    createProject({
      onSuccess: (response: ServiceResponse) => {
        setProducts(response.data.products || []);
        setUsers(response.data.users || []);
        hideLoading?.();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        toast.error(response.message);
      }
    });
    getCostCenters(defaultCostCenterFilter, {
      onSuccess: (response: ServiceResponse) => {
        setCostCenters(response.data?.cost_centers ?? []);
      }
    });
  };

  const store = () => {
    showLoading?.('loading', 'Guardando proyecto...');
    storeProject(normalizeProjectPayloadForApi(project), {
      onSuccess: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
        onSaved && onSaved();
      },
      onError: (response: ServiceResponse) => {
        hideLoading?.();
        useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
      },
      onFieldError: (errs: ServiceResponse) => {
        hideLoading?.();
        setErrorFields(errs.data);
      }
    });
  };

  return (
    <ProjectFormContainer
      fetching={fetchingCreateProject || fetchingStoreProject}
      action={store}
      cancel={onCancel}
      project={project}
      setProject={setProject}
      products={products}
      users={users}
      costCenters={costCenters}
      errorFields={errorFields}
    />
  );
};

export default ProjectCreate;